<script>
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from 'dashboard/routes/dashboard/conversation/ContactConversations.vue';
import ContactInfo from 'dashboard/routes/dashboard/conversation/contact/ContactInfo.vue';
import ContactLabel from 'dashboard/routes/dashboard/contacts/components/ContactLabels.vue';
import CustomAttributes from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributes.vue';
import Draggable from 'vuedraggable';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';

export default {
  components: {
    AccordionItem,
    ContactConversations,
    ContactInfo,
    ContactLabel,
    CustomAttributes,
    Draggable,
  },
  emits: ['contactRestored'],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const {
      updateUISettings,
      isContactSidebarItemOpen,
      contactSidebarItemsOrder,
      toggleSidebarUIState,
    } = useUISettings();
    const { isAdmin } = useAdmin();

    return {
      updateUISettings,
      isContactSidebarItemOpen,
      contactSidebarItemsOrder,
      toggleSidebarUIState,
      isAdmin,
    };
  },
  data() {
    return {
      dragEnabled: true,
      contactSidebarItems: [],
      dragging: false,
      isRestoring: false,
    };
  },
  computed: {
    hasContactAttributes() {
      const { custom_attributes: customAttributes } = this.contact;
      return customAttributes && Object.keys(customAttributes).length;
    },
    isContactDiscarded() {
      return Boolean(this.contact?.discarded || this.contact?.deleted_at);
    },
  },
  mounted() {
    this.contactSidebarItems = this.contactSidebarItemsOrder;
  },
  methods: {
    onDragEnd() {
      this.dragging = false;
      this.updateUISettings({
        contact_sidebar_items_order: this.contactSidebarItems,
      });
    },
    async restoreContact() {
      if (!this.contact?.id || this.isRestoring) return;
      this.isRestoring = true;
      try {
        await this.$store.dispatch('contacts/restore', this.contact.id);
        useAlert(this.$t('CONTACTS_PAGE.RESTORE_CONTACT_SUCCESS'));
        this.$emit('contactRestored', this.contact.id);
      } catch (error) {
        useAlert(this.$t('CONTACTS_PAGE.RESTORE_CONTACT_ERROR'));
      } finally {
        this.isRestoring = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="relative w-1/4 h-full min-h-screen overflow-y-auto text-sm bg-white dark:bg-slate-900 border-slate-50 dark:border-slate-800/50"
    :class="showAvatar ? 'border-l border-solid ' : 'border-r border-solid'"
  >
    <ContactInfo
      :show-close-button="showCloseButton"
      :show-avatar="showAvatar"
      :contact="contact"
      close-icon-name="dismiss"
      @panel-close="onClose"
      @toggle-panel="onClose"
    />
    <div
      v-if="isContactDiscarded"
      class="flex flex-col gap-2 p-3 mx-4 mt-2 mb-3 border border-red-200 rounded-md dark:border-red-700/40 bg-red-50 dark:bg-red-900/20"
      data-testid="contact-deleted-banner"
    >
      <div class="flex items-center gap-2">
        <fluent-icon icon="warning" size="16" class="text-red-700 dark:text-red-300" />
        <span class="text-sm font-medium text-red-700 dark:text-red-300">
          {{ $t('CONTACTS_PAGE.DELETED_BADGE') }}
        </span>
      </div>
      <woot-button
        v-if="isAdmin"
        size="small"
        color-scheme="success"
        icon="arrow-clockwise"
        :is-loading="isRestoring"
        :is-disabled="isRestoring"
        data-testid="restore-contact-button"
        @click="restoreContact"
      >
        {{ $t('CONTACTS_PAGE.RESTORE_CONTACT') }}
      </woot-button>
    </div>
    <div class="list-group">
      <Draggable
        :list="contactSidebarItems"
        :disabled="!dragEnabled"
        ghost-class="ghost"
        item-key="name"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div :key="element.name" class="list-group-item">
            <div v-if="element.name === 'contact_attributes'">
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
                :is-open="isContactSidebarItemOpen('is_ct_custom_attr_open')"
                compact
                @toggle="
                  value => toggleSidebarUIState('is_ct_custom_attr_open', value)
                "
              >
                <CustomAttributes
                  :contact-id="contact.id"
                  attribute-type="contact_attribute"
                  attribute-from="contact_panel"
                  :custom-attributes="contact.custom_attributes"
                  :empty-state-message="
                    $t('CONTACT_PANEL.SIDEBAR_SECTIONS.NO_RECORDS_FOUND')
                  "
                />
              </AccordionItem>
            </div>
            <div v-if="element.name === 'contact_labels'">
              <AccordionItem
                :title="$t('CONTACT_PANEL.SIDEBAR_SECTIONS.CONTACT_LABELS')"
                :is-open="isContactSidebarItemOpen('is_ct_labels_open')"
                @toggle="
                  value => toggleSidebarUIState('is_ct_labels_open', value)
                "
              >
                <ContactLabel :contact-id="contact.id" class="contact-labels" />
              </AccordionItem>
            </div>
            <div v-if="element.name === 'previous_conversation'">
              <AccordionItem
                :title="
                  $t('CONTACT_PANEL.SIDEBAR_SECTIONS.PREVIOUS_CONVERSATIONS')
                "
                :is-open="isContactSidebarItemOpen('is_ct_prev_conv_open')"
                compact
                @toggle="
                  value => toggleSidebarUIState('is_ct_prev_conv_open', value)
                "
              >
                <ContactConversations
                  v-if="contact.id"
                  :contact-id="contact.id"
                  conversation-id=""
                />
              </AccordionItem>
            </div>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply pb-3 mb-4;
  }
}

.list-group {
  .list-group-item {
    @apply bg-white dark:bg-slate-900;
  }
}

.conversation--details {
  @apply py-0 px-4;
}
</style>
